import React, { useState, useEffect } from 'react';
import { bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { propTypes } from '../../../../util/types';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';
import { ReactComponent as TopbarHomeIcon } from '../../../../assets/topbarHomeIcon.svg';
import { ReactComponent as MainLogo } from '../../../../assets/icons/mainLogo.svg';
import { ReactComponent as MessageSquareIcon } from '../../../../assets/icons/iconMessageSquare.svg';

import { ReactComponent as ListIcon } from '../../../../assets/icons/list.svg';
// import { ReactComponent as ProfileIcon } from '../../../../assets/icons/profile.svg';
import { ReactComponent as SettingsIcon } from '../../../../assets/icons/settings.svg';
import { ReactComponent as ExitIcon } from '../../../../assets/icons/exit.svg';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
// import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';

const ApplyLink = () => {
  return (
    <NamedLink
      name="ContactPage"
      className={css.applyLink}
      to={{ search: "?variant=apply" }}
    >
      <FormattedMessage id="TopbarDesktop.apply" />
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLink}>
      <span className={css.loginLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({
  notificationCount,
  currentUserHasListings,
  unreadMessagesLog,
}) => {
  const hasUnreadMessages = Object.values(unreadMessagesLog).some(i => i == true);
  // const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      className={css.inboxLink}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.inboxLinkLabel}>
        {/* <FormattedMessage id="TopbarDesktop.inbox" /> */}
        <MessageSquareIcon />
        {hasUnreadMessages ? <div className={css.notificationDot} /> : null}
        {/* {notificationDot} */}
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const isAdmin = !!currentUser?.attributes?.profile?.metadata?.isAdmin;

  return (
    <Menu className={css.profileMenuWrapper}>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="MyHubPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('MyHubPage'))}
            name="MyHubPage"
          >
            <TopbarHomeIcon />
            Home
          </NamedLink>
        </MenuItem>

        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <SettingsIcon />
            Settings
          </NamedLink>
        </MenuItem>

        {/* <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <SettingsIcon />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem> */}
        <MenuItem key="BlogPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('BlogPage'))}
            name="BlogPage"
          >
            <ListIcon />
            Archive
          </NamedLink>
        </MenuItem>

        <MenuItem key="AboutPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AboutPage'))}
            name="AboutPage"
          >
            <ListIcon />
            About
          </NamedLink>
        </MenuItem>

        <MenuItem key="AdminDashboardPage">
          {isAdmin ? <NamedLink
            className={classNames(css.menuLink, currentPageClass('AdminDashboardPage'))}
            name="AdminDashboardPage"
          >
            <SettingsIcon />
            <FormattedMessage id="TopbarDesktop.AdminDashboardLink" />
          </NamedLink> : null}
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <ExitIcon />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
    unreadMessagesLog,
    isHomePage,
  } = props;

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const rootClass = authenticatedOnClientSide ? css.root : css.rootNonLoggedIn;
  const classes = classNames(rootClassName || rootClass, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      notificationCount={notificationCount}
      currentUserHasListings={currentUserHasListings}
      unreadMessagesLog={unreadMessagesLog}
    />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu currentPage={currentPage} currentUser={currentUser} onLogout={onLogout} />
  ) : null;

  const applyLinkMaybe = isAuthenticatedOrJustHydrated ? null : (
    <ApplyLink />
  );
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : (
    <LoginLink />
  );

  return (
    <nav className={classes}>
      <div className={authenticatedOnClientSide ? css.navTopRow : css.navTopRowNonLoggedIn}>
        <LinkedLogo
          className={css.logoLink}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          linkToExternalSite={config?.topbar?.logoLink}
          mainLogo={<MainLogo style={{ marginTop: "25px" }} />}
          authenticated={authenticatedOnClientSide}
        />
        <TopbarSearchForm
          className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
          desktopInputRoot={css.topbarSearchWithLeftPadding}
          onSubmit={onSearchSubmit}
          initialValues={initialSearchFormValues}
          appConfig={config}
        />

        {authenticatedOnClientSide ? null : <div className={css.oldLinksMenu}>
          <NamedLink name="AboutPage" className={css.topbarLink}>
            <span className={css.topbarLinkLabel}>
              About
            </span>
          </NamedLink>

          <NamedLink name="BlogPage" className={css.topbarLink}>
            <span className={css.topbarLinkLabel}>
              Archive
            </span>
          </NamedLink>

          <NamedLink name="SearchPage" className={css.topbarLink}>
            <span className={css.topbarLinkLabel}>
              All Listings
            </span>
          </NamedLink>

          <NamedLink name="DebtPage" className={css.topbarLink}>
            <span className={css.topbarLinkLabel}>
              Debt
            </span>
          </NamedLink>

          <NamedLink name="NewListingPage" className={css.topbarLink}>
            <span className={css.topbarLinkLabel}>
              Create Listing
            </span>
          </NamedLink>
        </div>}

        {/* <CustomLinksMenu
          currentPage={currentPage}
          customLinks={customLinks}
          intl={intl}
          hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
        /> */}

        {/* {inboxLinkMaybe} */}
        {profileMenuMaybe}
        {loginLinkMaybe}
        {applyLinkMaybe}
      </div>
      {authenticatedOnClientSide ? <div className={css.navBottomRow}>
        <NamedLink name="MyHubPage">
          <TopbarHomeIcon className={css.homeIcon} />
        </NamedLink>

        {isHomePage ? <div className={css.homeTitle}>
          Home
        </div> : null}

        <NamedLink name="NewListingPage" className={css.topbarLinkButton}>
          Create Listing
        </NamedLink>

        <NamedLink name="FavoriteListingsPage" className={css.topbarLinkButton}>
          <FormattedMessage id="TopbarDesktop.favourites" />
        </NamedLink>

        <NamedLink name="InboxBasePage" className={css.topbarLinkButton}>
          Inbox
        </NamedLink>

        <NamedLink name="SearchPage" className={css.topbarLinkButton}>
          All Listings
        </NamedLink>

        {isHomePage ? <>
          <NamedLink name="ProfileSettingsPage" className={css.topbarLinkButton}>
            My Preferences
          </NamedLink>

          <NamedLink name="ManageListingsPage" className={css.topbarLinkButton}>
            My Listings
          </NamedLink>

          <NamedLink name="BlogPage" className={css.topbarLinkButton}>
            Archive
          </NamedLink>

          <NamedLink name="DebtPage" className={css.topbarLinkButton}>
            Debt
          </NamedLink>
        </> : null}
      </div> : <div className={css.navBottomRowWhite} />}
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  config: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  config: object,
};

export default TopbarDesktop;
